import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { investmentPerspectivesAPI, trackActivity, API_BASE_URL } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePreview } from '../../contexts/PreviewContext';
import BulkDownloadModal from './BulkDownloadModal';
import PublishModal from './PublishModal';
import WhitelabelModal from './WhitelabelModal';
import PositioningModal from './PositioningModal';
import HelpGuideModal from './HelpGuideModal';
import RtfUploadModal from '../../components/RtfUploadModal';
import ReportViewer from './ReportViewer';
import EditableContentPanel from './EditableContentPanel';
import PeriodSelector from './QuarterSelector';
import BrandList from './BrandList';
import './styles.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Title, Tooltip, Legend, TimeScale, Filler
);

const REPORT_NAME = 'Investment Perspectives';

const InvestmentPerspectivesApp = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { previewDomain, isPreviewMode } = usePreview();

  const [selectedPeriod, setSelectedPeriod] = useState(null);   // 'YYYY-MM'
  const [selectedDomain, setSelectedDomain] = useState('_default');
  const [brands, setBrands] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isRtfLoading, setIsRtfLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('view');
  const [liveContent, setLiveContent] = useState(null);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isWhitelabelOpen, setIsWhitelabelOpen] = useState(false);
  const [isPositioningOpen, setIsPositioningOpen] = useState(false);

  // Overflow detection — tracks which blocks have content overflow
  const [overflowBlocks, setOverflowBlocks] = useState(new Set());

  // Inline layout editing state
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (viewMode !== 'edit') setLayoutEditMode(false);
  }, [viewMode]);

  const isDrummondUser = user?.is_drummond_user;
  const brandDomain = isDrummondUser && selectedDomain && selectedDomain !== '_default'
    ? selectedDomain : null;

  // Content model: Drummond (_default) is the master. Non-tailored clients always
  // mirror Drummond's content (via the backend fallback) + their branding, so they
  // are view-only and never get their own content row. Tailored clients are
  // standalone — editable, written from scratch or propagated to. White-labelled
  // brands (per month, from Manage White-Label — the report payload carries the
  // flag) are ALSO editable: saving creates their own content row for that month,
  // which then overrides the Drummond mirror until deleted.
  const selectedBrand = brands.find((b) => b.domain === selectedDomain);
  const isWhitelabelBrand = !!reportData?.whitelabel;
  const isMirrorClient = isDrummondUser && selectedDomain !== '_default'
    && !selectedBrand?.tailored && !isWhitelabelBrand;
  const canEdit = isDrummondUser
    && (selectedDomain === '_default' || !!selectedBrand?.tailored || isWhitelabelBrand);


  // A mirror client is never editable — force back to view mode if selected.
  useEffect(() => {
    if (!canEdit && viewMode === 'edit') setViewMode('view');
  }, [canEdit, viewMode]);

  // Chart refs for PDF export (dynamic template charts)
  const dynamicChartRefs = useRef({});

  // Track activity on mount + load brand list for the white-label preview
  useEffect(() => {
    trackActivity('app_access', 'investment_perspectives');
    if (isDrummondUser) {
      investmentPerspectivesAPI.getBrands()
        .then((res) => setBrands(res?.brands || []))
        .catch(() => { /* non-fatal */ });
    }
  }, [isDrummondUser]);

  // ---- Report load (period or domain change) ----
  const loadReportData = useCallback(async (period, cancelled) => {
    if (!period) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await investmentPerspectivesAPI.getReportData(period, brandDomain);
      if (cancelled?.current) return;
      if (result.success) {
        const d = result.data;
        // Compatibility aliases so the shared editor/viewer (built for portfolio
        // reports) can read the period as its "quarter".
        setReportData({
          ...d,
          quarter: d.period,
          quarter_display: d.period_display,
          portfolio_name: REPORT_NAME,
          portfolio_code: 'INVESTMENT_PERSPECTIVES',
        });
      } else {
        setError(result.message || 'Failed to load report');
      }
    } catch (err) {
      if (cancelled?.current) return;
      if (err.response?.status === 403) setError('This report is not published yet.');
      else setError(err.response?.data?.detail || 'Error loading report');
    } finally {
      if (!cancelled?.current) setIsLoading(false);
    }
  }, [brandDomain]);

  const loadContentOnly = useCallback(async (period) => {
    if (!period) return;
    try {
      const content = await investmentPerspectivesAPI.getContentOnly(period, brandDomain);
      setReportData((prev) => (prev ? { ...prev, content } : prev));
    } catch (err) {
      console.error('Error loading content:', err);
    }
  }, [brandDomain]);

  useEffect(() => {
    if (!selectedPeriod) return;
    const cancelled = { current: false };
    loadReportData(selectedPeriod, cancelled);
    return () => { cancelled.current = true; };
  }, [selectedPeriod, brandDomain, loadReportData]);

  // ---- Content save ----
  const handleContentSave = useCallback(async (content) => {
    if (!selectedPeriod || !canEdit) return;
    try {
      await investmentPerspectivesAPI.saveContent(selectedPeriod, content, selectedDomain || '_default');
      loadContentOnly(selectedPeriod);
    } catch (err) {
      console.error('Error saving content:', err);
      throw err;
    }
  }, [selectedPeriod, selectedDomain, loadContentOnly]);

  // ---- WYSIWYG capture (portfolio-agnostic) ----
  const fetchImageAsDataUrl = useCallback(async (url) => {
    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  const captureReportHTML = useCallback(async () => {
    const reportEl = document.querySelector('.report-pages');
    if (!reportEl) return null;
    const clone = reportEl.cloneNode(true);

    // Inline <img> to data URLs
    const liveImages = reportEl.querySelectorAll('img');
    const cloneImages = clone.querySelectorAll('img');
    const imgPromises = [];
    liveImages.forEach((img, i) => {
      if (!cloneImages[i]) return;
      const cloneImg = cloneImages[i];
      const originalSrc = img.getAttribute('data-original-src');
      let fetchSrc = originalSrc || img.src;
      if (API_BASE_URL && fetchSrc.startsWith(API_BASE_URL)) fetchSrc = fetchSrc.slice(API_BASE_URL.length);
      if (!originalSrc || originalSrc === img.src) {
        try {
          if (img.complete && img.naturalWidth > 0) {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            cloneImg.src = c.toDataURL('image/png');
            return;
          }
        } catch (e) { /* CORS — fall through */ }
      }
      if (fetchSrc && !fetchSrc.startsWith('data:')) {
        imgPromises.push(
          fetchImageAsDataUrl(fetchSrc)
            .then((d) => { cloneImg.src = d; })
            .catch(() => fetchImageAsDataUrl('/logo-name.png').then((d) => { cloneImg.src = d; }).catch(() => {}))
        );
      }
    });
    await Promise.all(imgPromises);

    // Canvas charts -> high-DPI <img>
    const liveCanvases = reportEl.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');
    liveCanvases.forEach((canvas, i) => {
      try {
        const chartInstance = ChartJS.getChart(canvas);
        let dataUrl;
        if (chartInstance) {
          const origDPR = chartInstance.options.devicePixelRatio;
          const origAnim = chartInstance.options.animation;
          chartInstance.options.animation = false;
          chartInstance.options.devicePixelRatio = 3;
          chartInstance.stop();
          chartInstance.resize();
          chartInstance.update('none');
          dataUrl = canvas.toDataURL('image/png', 1.0);
          chartInstance.options.devicePixelRatio = origDPR;
          chartInstance.options.animation = origAnim;
          chartInstance.resize();
          chartInstance.update('none');
        } else {
          dataUrl = canvas.toDataURL('image/png', 1.0);
        }
        const img = document.createElement('img');
        img.src = dataUrl;
        img.setAttribute('data-chart-img', 'true');
        img.style.width = '100%'; img.style.height = '100%'; img.style.display = 'block';
        if (cloneCanvases[i]?.parentNode) cloneCanvases[i].parentNode.replaceChild(img, cloneCanvases[i]);
      } catch (e) { /* ignore */ }
    });

    const styleBlocks = [];
    for (const sheet of document.styleSheets) {
      try {
        const rules = [];
        for (const rule of sheet.cssRules) {
          if (rule.type === CSSRule.IMPORT_RULE || rule.type === CSSRule.CHARSET_RULE || rule.type === CSSRule.NAMESPACE_RULE) continue;
          rules.push(rule.cssText);
        }
        if (rules.length) styleBlocks.push(`<style>${rules.join('\n')}</style>`);
      } catch (e) { /* CORS — skip */ }
    }
    const inlineStyle = reportEl.getAttribute('style') || '';
    const varOverrides = inlineStyle.split(';').filter((s) => s.trim().startsWith('--')).join('; ');
    if (varOverrides) styleBlocks.push(`<style>.report-pages { ${varOverrides} }</style>`);

    // Brand CSS vars live inline on the APP ROOT (not .report-pages), which the
    // capture doesn't include — re-inject them from the report payload so PDF
    // title/heading colours match the live view for branded domains.
    const safeHex = (v, fallback) => (/^#[0-9a-fA-F]{3,8}$/.test(v || '') ? v : fallback);
    const wlBranding = reportData?.whitelabel ? reportData?.branding : null;
    styleBlocks.push(`<style>.ip-app { --primary-navy: ${safeHex(wlBranding?.primary_color, '#072040')}; --secondary-gold: ${safeHex(wlBranding?.secondary_color, '#f0a500')}; }</style>`);

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
${styleBlocks.join('\n')}
</head>
<body class="print-capture ip-print-capture">
<div class="portfolio-reports-app ip-app report-pages print-capture-pages" style="${inlineStyle.replace(/"/g, '&quot;')}">
${clone.innerHTML}
</div>
</body>
</html>`;
  }, [fetchImageAsDataUrl, reportData]);

  const handleDownloadPdf = useCallback(async () => {
    if (!selectedPeriod) return;
    setIsPdfLoading(true);
    try {
      const wasEditing = viewMode === 'edit';
      if (wasEditing) {
        flushSync(() => setViewMode('view'));
        await new Promise((r) => requestAnimationFrame(r));
      }
      const htmlContent = await captureReportHTML();
      if (wasEditing) setViewMode('edit');
      const response = await investmentPerspectivesAPI.downloadPdf(selectedPeriod, htmlContent, brandDomain);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      link.setAttribute('download', disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `${selectedPeriod}_Investment_Perspectives.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Error generating PDF');
    } finally {
      setIsPdfLoading(false);
    }
  }, [selectedPeriod, viewMode, captureReportHTML, brandDomain]);

  // Editable text-only Word (RTF) export of the commentary — Drummond staff only.
  const handleDownloadWord = useCallback(async () => {
    if (!selectedPeriod) return;
    setIsRtfLoading(true);
    try {
      const response = await investmentPerspectivesAPI.downloadRtf(selectedPeriod, brandDomain);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      link.setAttribute('download', disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `${selectedPeriod}_Investment_Perspectives_text.rtf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading Word:', err);
      setError('Error generating Word document');
    } finally {
      setIsRtfLoading(false);
    }
  }, [selectedPeriod, brandDomain]);

  return (
    <div
      className={`portfolio-reports-app ip-app ${theme}`}
    >
      {/* Sidebar */}
      <div className="reports-sidebar open">
        <div className="sidebar-content">
          <div className="ip-sidebar-title-row">
            <h3 className="sidebar-title">Investment Perspectives</h3>
            {isDrummondUser && (
              <button
                className="ip-help-btn"
                onClick={() => setIsHelpOpen(true)}
                title="How the monthly process and publishing works"
              >
                ?
              </button>
            )}
          </div>

          <PeriodSelector
            selectedPeriod={selectedPeriod}
            onSelectPeriod={setSelectedPeriod}
          />

          {isDrummondUser && (
            <BrandList
              brands={brands}
              selectedDomain={selectedDomain}
              onSelect={setSelectedDomain}
            />
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="reports-main">
        <div className="reports-toolbar">
          <div className="toolbar-left">
            {reportData && (
              <h2 className="report-title">
                {REPORT_NAME} — {reportData.quarter_display}
              </h2>
            )}
          </div>

          <div className="toolbar-right">
            {canEdit && reportData && (
              <button
                className={`btn-mode ${viewMode === 'edit' ? 'active' : ''}`}
                onClick={() => setViewMode(viewMode === 'edit' ? 'view' : 'edit')}
              >
                {viewMode === 'edit' ? 'View Mode' : 'Edit Mode'}
              </button>
            )}
            {reportData && (
              <button className="btn-download" onClick={handleDownloadPdf} disabled={isPdfLoading}>
                {isPdfLoading ? 'Generating...' : 'Download PDF'}
              </button>
            )}
            {isDrummondUser && reportData && (
              <button
                className="btn-download"
                onClick={handleDownloadWord}
                disabled={isRtfLoading}
                title="Editable text-only Word file for editing commentary and pasting back in"
              >
                {isRtfLoading ? 'Generating...' : 'Download Word (text)'}
              </button>
            )}
            {canEdit && reportData && (
              <button
                className="btn-download"
                onClick={() => setIsUploadModalOpen(true)}
                title="Upload an edited Word (RTF) file to repopulate the report text"
              >
                Upload Word (text)
              </button>
            )}
            {isDrummondUser && selectedPeriod && (
              <button className="btn-download" onClick={() => setIsBulkModalOpen(true)}>
                Bulk Download
              </button>
            )}
            {isDrummondUser && selectedPeriod && (
              <button className="btn-publish" onClick={() => setIsPublishModalOpen(true)}>
                Manage Publishing
              </button>
            )}
            {isDrummondUser && (
              <button
                className="btn-download"
                onClick={() => setIsWhitelabelOpen(true)}
                title="Choose which domains get their own colour and logo on the cover"
              >
                Manage White-Label
              </button>
            )}
            {isDrummondUser && (
              <button
                className="btn-download"
                onClick={() => setIsPositioningOpen(true)}
                title="Choose which Dashboard suite each tailored client's positioning tables average"
              >
                Manage Positioning
              </button>
            )}
          </div>
        </div>

        {isMirrorClient && reportData && (
          <div className="ip-mirror-note">
            This client mirrors the Drummond (House) report. To change its content, edit the Drummond report; branding is applied automatically.
          </div>
        )}

        {isWhitelabelBrand && !selectedBrand?.tailored && selectedDomain !== '_default' && reportData && (
          <div className="ip-mirror-note">
            White-labelled this month: it mirrors the Drummond report until you edit and
            save, which creates this client's own version of the month.
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}

        {isLoading && (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>Loading report data...</p>
          </div>
        )}

        {!isLoading && !reportData && !error && (
          <div className="empty-state">
            <p>Select a month to view the report.</p>
          </div>
        )}

        {!isLoading && reportData && (
          <div className={`fact-sheet-wrapper ${viewMode === 'edit' ? 'split-view' : ''}`}>
            <div className="fact-sheet-content">
              <ReportViewer
                data={viewMode === 'edit' && liveContent
                  ? { ...reportData, content: { ...reportData.content, ...liveContent } }
                  : reportData}
                dynamicChartRefs={dynamicChartRefs}
                onOverflowChange={setOverflowBlocks}
                layoutEditMode={layoutEditMode && canEdit}
                onBlockChange={(pageIndex, blockId, updated) => {
                  editorRef.current?.handleBlockChange(pageIndex, blockId, updated);
                }}
                onBlocksChange={(pageIndex, updates) => {
                  editorRef.current?.handleBlocksChange(pageIndex, updates);
                }}
                onRemoveBlock={(blockId) => {
                  editorRef.current?.handleRemoveBlock(blockId);
                }}
                onAddBlock={(pageIndex) => {
                  editorRef.current?.handleAddBlock(pageIndex);
                }}
                onToggleFooter={(pageIndex) => {
                  editorRef.current?.togglePageFooter?.(pageIndex);
                }}
                onReorderBlock={(pageIndex, blockId, toFront) => {
                  editorRef.current?.reorderBlock?.(pageIndex, blockId, toFront);
                }}
                overflowBlocks={overflowBlocks}
                customBlocks={liveContent?.custom_blocks || reportData.content?.custom_blocks}
                editMode={viewMode === 'edit' && canEdit}
                onTextChange={(key, html) => { editorRef.current?.setField?.(key, html); }}
                onCustomTextChange={(blockId, html) => { editorRef.current?.setCustomContent?.(blockId, html); }}
                onHeadingChange={(blockId, text) => { editorRef.current?.setHeading?.(blockId, text); }}
                onHeadingStyleChange={(blockId, styles) => { editorRef.current?.setHeadingStyle?.(blockId, styles); }}
                onBlockDefChange={(blockId, blockDef) => { editorRef.current?.setBlockDef?.(blockId, blockDef); }}
              />
            </div>

            {viewMode === 'edit' && canEdit && (
              <div className="factsheet-right-panel">
                <div className="edit-panel-content">
                  <EditableContentPanel
                    ref={editorRef}
                    content={reportData.content}
                    portfolioCode={reportData.portfolio_code}
                    portfolioName={reportData.portfolio_name}
                    quarter={selectedPeriod}
                    branding={reportData.branding}
                    onSave={handleContentSave}
                    onChange={setLiveContent}
                    brands={brands}
                    domain={selectedDomain}
                    overflowBlocks={overflowBlocks}
                    onLayoutModeChange={setLayoutEditMode}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <BulkDownloadModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        isDrummondUser={isDrummondUser}
        period={selectedPeriod}
        brands={brands}
      />

      <PublishModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        period={selectedPeriod}
        isDrummondUser={isDrummondUser}
        brands={brands}
      />

      {isDrummondUser && (
        <HelpGuideModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
      )}

      {isDrummondUser && (
        <WhitelabelModal
          isOpen={isWhitelabelOpen}
          onClose={() => setIsWhitelabelOpen(false)}
          isDrummondUser={isDrummondUser}
          brands={brands}
          period={selectedPeriod}
        />
      )}

      {isDrummondUser && (
        <PositioningModal
          isOpen={isPositioningOpen}
          onClose={() => setIsPositioningOpen(false)}
          isDrummondUser={isDrummondUser}
          onSaved={() => { if (selectedPeriod) loadReportData(selectedPeriod); }}
        />
      )}

      <RtfUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Upload edited Word file"
        parseFn={(f) => investmentPerspectivesAPI.parseRtf(selectedPeriod, f, brandDomain)}
        applyFn={(f) => investmentPerspectivesAPI.applyRtf(selectedPeriod, f, brandDomain)}
        onApplied={() => loadReportData(selectedPeriod)}
      />
    </div>
  );
};

export default InvestmentPerspectivesApp;
